/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import isNil from 'lodash-es/isNil';
import { ACTIVITY_HISTORY_SORT_COLUMN } from '@hems/util/src/constant/activityHistory';
import { FORM_TYPE, APP_MODE, AC_SYSTEM_FIELD_TYPE_CODE } from '@hems/util/src/constant/constant';
import { ENERGY_CONTROL } from '@hems/util/src/constant/dashboard';
import { GEN_TYPE } from '@hems/util/src/constant/device';
import { ERROR_LEVEL } from '@hems/util/src/constant/eventHistory';
import { FIRMWARE_UPDATE_STATUS, FIRMWARE_UPDATE_TYPE, SITE_CONFIGURATION_TYPE, } from '@hems/util/src/constant/firmwareUpdate';
import { GRAPH_GROUP_CODE } from '@hems/util/src/constant/graph';
import { APPLIANCE_TYPE } from '@hems/util/src/constant/homeAppliance';
import { LANGUAGE } from '@hems/util/src/constant/languages';
import { FLEET_ROLE } from '@hems/util/src/constant/role';
import { SETTING_TARGET, OVER_09_EMS_VERSION_GRID_CODE, ENERGY_POLICY } from '@hems/util/src/constant/settings';
import { UNIT } from '@hems/util/src/constant/unit';
import { isEmptyString } from '@hems/util/src/helper/helper';
export const isStringType = (value) => {
    return typeof value === 'string';
};
export const isFormModeType = (value) => {
    return value === FORM_TYPE.READ || value === FORM_TYPE.EDIT || value === FORM_TYPE.NEW;
};
export const getRouteInfo = (value, isTypeGuardValue) => {
    if (isTypeGuardValue(value))
        return value;
    return;
};
export const isPeriod = (date) => {
    return 'start' in date && 'end' in date;
};
export const isNumberType = (value) => {
    return typeof value === 'number';
};
export const getStringValue = (value) => {
    if (value === undefined || value === null || isEmptyString(value.trim())) {
        return null;
    }
    return value;
};
export const isDeviceACType = (device) => {
    return device.genType === GEN_TYPE.AC_SYS;
};
export const isACSystemType = (deviceInfo) => {
    return deviceInfo.gen_type === GEN_TYPE.AC_SYS;
};
export const isGraphGroupCodeType = (value) => {
    const graphGroupCodeList = Object.values(GRAPH_GROUP_CODE);
    return graphGroupCodeList.includes(value);
};
export const getGraphGroupCodeTypeValue = (value) => {
    if (isGraphGroupCodeType(value)) {
        return value;
    }
    return null;
};
export const isGraphGroupCodeListType = (value) => {
    const graphGroupCodeList = Object.values(GRAPH_GROUP_CODE);
    return value.every((item) => graphGroupCodeList.includes(item));
};
export const getGraphGroupCodeListTypeValue = (value) => {
    if (isGraphGroupCodeListType(value)) {
        return value;
    }
    return null;
};
export const isErrorCodeDisplayDetails = (errorDetails) => errorDetails !== null && typeof errorDetails === 'object' && 'description' in errorDetails;
export const isObjectType = (value) => {
    return Object.prototype.toString.call(value).slice(8, -1) === 'Object';
};
export const isDisplayedSiteErrorInfoType = (value) => Array.isArray(value) &&
    value.every((item) => isObjectType(item) && item && 'errorCode' in item && 'triggerDate' in item);
export const isEnergyControlType = (operationMode) => {
    const energyControlValues = Object.values(ENERGY_CONTROL);
    return energyControlValues.includes(operationMode);
};
export const isGroupSelectOptionListType = (optionList) => Array.isArray(optionList) && optionList.every((option) => isObjectType(option) && option && 'items' in option);
export const isSelectOptionListType = (optionList) => Array.isArray(optionList) &&
    optionList.every((option) => isObjectType(option) && 'text' in option && 'value' in option);
export const isSiteStatusCodeType = (value) => value === '0' || value === '1' || value === '2' || value === '4' || value === '6';
export const isAuthSuccess = (authResponse) => isObjectType(authResponse) && 'access_token' in authResponse;
export const isSettingTargetType = (value) => {
    const settingTargetValues = Object.values(SETTING_TARGET);
    return settingTargetValues.includes(value);
};
export const isAppModeValueType = (value) => value === APP_MODE.LOCAL ||
    value === APP_MODE.DEVELOPMENT ||
    value === APP_MODE.STAGING ||
    value === APP_MODE.PRODUCTION;
export const isLocaleValueType = (value) => {
    const localeList = Object.values(LANGUAGE);
    return localeList.includes(value);
};
export const isDeviceInfoType = (item) => 'total_device' in item;
export const isESSNumberType = (ESSNumber) => [1, 2, 3, 4].includes(ESSNumber);
export const getESSNumber = (ESSNumber) => isESSNumberType(ESSNumber) ? ESSNumber : null;
export const isUnitValueType = (unit) => {
    const unitValueList = Object.values(UNIT);
    return unitValueList.includes(unit);
};
export const isLanguageValueType = (lang) => {
    const languageList = Object.values(LANGUAGE);
    return languageList.includes(lang);
};
export const getLanguageValue = (lang) => (isLanguageValueType(lang) ? lang : LANGUAGE.EN);
export const isSupportedApplianceType = (applianceType) => {
    const supportedApplianceList = Object.values(APPLIANCE_TYPE);
    return supportedApplianceList.includes(applianceType);
};
export const isNonNullableType = (value) => !isNil(value);
export const isGridCheckEventType = (event) => {
    return typeof event === 'object' && event !== null && 'rowKey' in event;
};
export const isGridEventType = (event) => {
    return typeof event === 'object' && event !== null;
};
export const isHTMLElementType = (value) => {
    return value instanceof HTMLElement;
};
export const isSiteConfigurationType = (value) => value === SITE_CONFIGURATION_TYPE.SOLAR ||
    value === SITE_CONFIGURATION_TYPE.BACKUP ||
    value === SITE_CONFIGURATION_TYPE.GRID;
export const isFirmwareUpdateVersionType = (key) => {
    return ['emsVersion', 'pcsVersion', 'gemVersion', 'hubVersion', 'bmsVersion'].includes(key);
};
export const isFirmwareUpdateFileType = (key) => {
    return ['emsFileName', 'pcsFileName', 'gemFileName', 'hubFileName', 'bmsFileName'].includes(key);
};
export const isFirmwareUpdateEMSFileType = (key) => {
    return ['appFileName', 'fullFileName'].includes(key);
};
export const isFirmwareUpdateType = (value) => value === FIRMWARE_UPDATE_TYPE.AUTO || value === FIRMWARE_UPDATE_TYPE.MANUAL;
export const isFirmwareUpdateStatusType = (value) => value === FIRMWARE_UPDATE_STATUS.NONE ||
    value === FIRMWARE_UPDATE_STATUS.DOWNLOAD_STARTED ||
    value === FIRMWARE_UPDATE_STATUS.UPDATE_STARTED;
export const isErrorLevelType = (value) => {
    return value !== null && Object.values(ERROR_LEVEL).includes(value);
};
export const isEMS09GridCodeProfileName = (name) => {
    return name === OVER_09_EMS_VERSION_GRID_CODE.LUMA || name === OVER_09_EMS_VERSION_GRID_CODE.IEEE_1547_2018_EXTEND;
};
export const isActivityHistoryAvailableSortColumnType = (value) => {
    const availableSortColumnList = Object.values(ACTIVITY_HISTORY_SORT_COLUMN);
    return availableSortColumnList.includes(value);
};
export const isEnergyPolicyType = (value) => {
    const energyPolicyValues = Object.values(ENERGY_POLICY);
    return energyPolicyValues.includes(value);
};
export const isFleetRoleType = (value) => {
    const fleetRoleValues = Object.values(FLEET_ROLE);
    return fleetRoleValues.includes(value);
};
export const isDeviceSettingTargetType = (value) => {
    const settingTargetValues = Object.values(AC_SYSTEM_FIELD_TYPE_CODE);
    return settingTargetValues.includes(value);
};
export const isPhysicalUnitType = (unit) => unit !== UNIT.PERCENT &&
    unit !== UNIT.CELSIUS &&
    unit !== UNIT.FACTOR &&
    unit !== UNIT.DEGREE &&
    unit !== UNIT.FAHRENHEIT;
